<script setup lang="ts">
import { computed } from 'vue'

import type { ChatMessage } from '../models/orpheus.types'
import { normalizeAnswerPayload } from '../service/answerPayload'

import AnswerTable from './AnswerTable.vue'

import { useMetricCatalog } from '@/features/reports'
import { useI18n } from '@/shared/i18n'
import { useFormat } from '@/shared/lib/format'
import { Button } from '@/shared/ui'

const props = withDefaults(
  defineProps<{ message: ChatMessage; active?: boolean; preview?: boolean }>(),
  { active: false, preview: true },
)
const emit = defineEmits<{ 'open-answer': [] }>()
const { t } = useI18n()
const format = useFormat()
const catalog = useMetricCatalog()

const projection = computed(() =>
  props.message.answer
    ? normalizeAnswerPayload(props.message.answer, { getColumn: catalog.getColumn })
    : null,
)
const rows = computed(() => projection.value?.rows ?? [])
const range = computed(() => {
  const dateRange = props.message.answer?.dateRange
  if (dateRange?.start && dateRange.end) {
    return `${format.formatDate(dateRange.start)} – ${format.formatDate(dateRange.end)}`
  }
  if (dateRange?.start || dateRange?.end) {
    return format.formatDate(dateRange.start ?? dateRange.end)
  }
  if (dateRange?.days) return t('orpheus.message.lookback', { count: dateRange.days })
  return t('orpheus.message.allDates')
})
const subtitle = computed(() => {
  const count = props.message.answer?.resultCount ?? rows.value.length
  return t(
    count === 1 ? 'orpheus.message.reportSubtitleOne' : 'orpheus.message.reportSubtitleMany',
    {
      count: format.formatInteger(count),
      range: range.value,
    },
  )
})
</script>

<template>
  <section
    v-if="message.answer && rows.length > 0"
    class="report-strip grid gap-xs mt-sm overflow-hidden border border-border rounded-md bg-surface"
    :class="{ 'report-strip--active': active }"
  >
    <AnswerTable
      v-if="preview && !active && projection"
      class="report-strip__preview max-h-48 overflow-hidden"
      :rows="rows"
      :columns="projection.columns"
      :total="projection.summaryTotal"
      preview
    />
    <div
      class="report-strip__summary flex items-center justify-between gap-sm py-xs px-sm text-muted text-[0.75rem]"
    >
      <span>{{ subtitle }}</span>
      <Button size="xs" variant="data" @click="emit('open-answer')">
        {{ t('orpheus.message.viewFullReport') }}
      </Button>
    </div>
  </section>
</template>

<style scoped>
.report-strip.report-strip--active {
  border-color: var(--ui-color-orpheus-muted);
  background-color: var(--ui-color-orpheus-soft);
}
</style>
